/**
 * Pixi screens outside play (Bible §4): the Surface title and the Shaft level select. Built from
 * the same SpriteAtlas as the game so real art drops in by name. No story text until WP8.
 */
import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { ASSETS, BUILD_STAMP, GAME_NAME, SHAFT_LABEL } from '../../config.ts';
import type { Layout } from '../layout.ts';
import { PALETTE } from '../palette.ts';
import { makeText, type SpriteAtlas } from './textures.ts';

/** Cover-fit a sprite over a w×h box (crop overflow, keep aspect), centred. */
function cover(sp: Sprite, w: number, h: number, focusY = 0.5): void {
  const tw = sp.texture.width;
  const th = sp.texture.height;
  const s = Math.max(w / tw, h / th);
  sp.scale.set(s);
  sp.anchor.set(0.5, focusY);
  sp.position.set(w / 2, h * focusY);
}

interface Btn {
  root: Container;
  bg: Sprite;
  label: Text;
}

function button(tex: Texture, text: string, w: number, h: number, fontPx: number, color: number = PALETTE.hudText): Btn {
  const root = new Container();
  const bg = new Sprite(tex);
  bg.anchor.set(0.5);
  bg.width = w;
  bg.height = h;
  const label = makeText(text, fontPx, { bold: true, color, align: 'center' });
  label.anchor.set(0.5);
  root.addChild(bg, label);
  root.eventMode = 'static';
  root.cursor = 'pointer';
  root.on('pointerdown', () => root.scale.set(0.96));
  root.on('pointerup', () => root.scale.set(1));
  root.on('pointerupoutside', () => root.scale.set(1));
  return { root, bg, label };
}

export interface TitleHooks {
  onDig(): void;
  onGauntlet(): void;
  onSettings(): void;
  /** 2 s long-press on the wordmark opens the dev menu. */
  onDevMenu(): void;
  nextLevelLabel(): string;
  gauntletBest(): number;
}

export class TitleScreen {
  readonly root = new Container();
  private readonly bg: Sprite;
  private readonly shade = new Graphics();
  private readonly wordmark: Text;
  private readonly dig: Btn;
  private readonly gauntlet: Btn;
  private readonly settings: Btn;
  private readonly stamp: Text;
  /** A1: the small "Shaft 1 · Level N" line under the big "Dig". */
  private readonly digSub: Text;
  private pressTimer: ReturnType<typeof setTimeout> | null = null;
  private t = 0;
  private readonly atlasFn: () => SpriteAtlas;
  private readonly hooks: TitleHooks;
  private get atlas(): SpriteAtlas {
    return this.atlasFn();
  }

  constructor(atlas: () => SpriteAtlas, hooks: TitleHooks) {
    this.atlasFn = atlas;
    this.hooks = hooks;
    this.bg = new Sprite(this.atlas.get(ASSETS.scene.surface));
    this.wordmark = new Text({ text: GAME_NAME, style: { fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', fontSize: 56, fontWeight: '900', fill: PALETTE.uiTrim, stroke: { color: 0x4a2a12, width: 8, join: 'round' }, letterSpacing: 2 }, resolution: 2 });
    this.wordmark.anchor.set(0.5);
    this.wordmark.eventMode = 'static';
    this.wordmark.on('pointerdown', () => {
      this.pressTimer = setTimeout(() => hooks.onDevMenu(), 2000);
    });
    const cancel = (): void => {
      if (this.pressTimer) clearTimeout(this.pressTimer);
      this.pressTimer = null;
    };
    this.wordmark.on('pointerup', cancel);
    this.wordmark.on('pointerupoutside', cancel);
    this.dig = button(this.atlas.get(ASSETS.ui.cta), 'Dig', 240, 78, 30, 0x2a1408);
    this.digSub = makeText('', 12, { bold: true, color: 0x2a1408, align: 'center' });
    this.digSub.anchor.set(0.5);
    this.digSub.alpha = 0.8;
    this.dig.root.addChild(this.digSub);
    this.gauntlet = button(this.atlas.get(ASSETS.ui.secondary), 'Gauntlet', 200, 60, 20);
    this.settings = button(this.atlas.get(ASSETS.ui.tinSign), '⚙', 64, 54, 26, 0x2a2e4a);
    this.dig.root.on('pointertap', () => hooks.onDig());
    this.gauntlet.root.on('pointertap', () => hooks.onGauntlet());
    this.settings.root.on('pointertap', () => hooks.onSettings());
    this.stamp = makeText('', 10, { color: 0xfff8e8 });
    this.stamp.alpha = 0.7;
    this.root.addChild(this.bg, this.shade, this.wordmark, this.dig.root, this.gauntlet.root, this.settings.root, this.stamp);
  }

  refreshTextures(): void {
    this.bg.texture = this.atlas.get(ASSETS.scene.surface);
    this.dig.bg.texture = this.atlas.get(ASSETS.ui.cta);
    this.gauntlet.bg.texture = this.atlas.get(ASSETS.ui.secondary);
    this.settings.bg.texture = this.atlas.get(ASSETS.ui.tinSign);
  }

  layout(L: Layout): void {
    const { w, h } = L;
    cover(this.bg, w, h, 0.45);
    this.shade.clear();
    // Soft dark ramp behind the buttons so the CTA reads on the dirt path.
    for (let i = 0; i < 8; i++) this.shade.rect(0, h * (0.66 + i * 0.045), w, h * 0.05).fill({ color: 0x2a1408, alpha: 0.05 + i * 0.025 });
    this.wordmark.style.fontSize = Math.round(Math.min(64, w * 0.15));
    this.wordmark.position.set(w / 2, L.insets.top + h * 0.09);
    const bw = Math.min(280, w * 0.66);
    this.dig.bg.width = bw;
    this.dig.bg.height = bw * 0.33;
    this.dig.label.style.fontSize = Math.round(bw * 0.15);
    this.dig.label.position.set(0, -bw * 0.035);
    this.digSub.style.fontSize = Math.round(bw * 0.058);
    this.digSub.position.set(0, bw * 0.095);
    this.dig.root.position.set(w / 2, h * 0.78);
    this.gauntlet.bg.width = bw * 0.72;
    this.gauntlet.bg.height = bw * 0.22;
    this.gauntlet.label.style.fontSize = Math.round(bw * 0.075);
    this.gauntlet.root.position.set(w / 2, h * 0.78 + bw * 0.33);
    this.settings.root.position.set(w - L.insets.right - 44, L.insets.top + 40);
    this.stamp.position.set(L.insets.left + 8, h - L.insets.bottom - 16);
  }

  update(dt: number): void {
    this.t += dt;
    this.dig.label.text = 'Dig';
    this.digSub.text = this.hooks.nextLevelLabel();
    const best = this.hooks.gauntletBest();
    this.gauntlet.label.text = best ? `Gauntlet · best ${best}` : 'Gauntlet';
    this.stamp.text = `build ${BUILD_STAMP}`;
    // CTA breathes.
    const s = 1 + 0.02 * Math.sin(this.t / 500);
    if (this.dig.root.scale.x > 0.99) this.dig.root.scale.set(s);
  }
}

export interface ShaftLevel {
  id: string;
  name: string;
  index: number;
  hard: boolean;
  cleared: boolean;
  stars: number;
  /** The glowing lamp: first uncleared level. */
  next: boolean;
  locked: boolean;
}

export interface ShaftHooks {
  onPlay(index: number): void;
  onBack(): void;
  levels(): ShaftLevel[];
}

export class ShaftScreen {
  readonly root = new Container();
  private readonly bg: Sprite;
  private readonly sign: Sprite;
  private readonly signT: Text;
  private readonly back: Btn;
  private readonly lamps: { root: Container; lamp: Sprite; num: Text; stars: Sprite[]; tag: Sprite; glow: Sprite; bounceAt: number; shakeAt: number }[] = [];
  private t = 0;
  private L: Layout | null = null;
  private readonly atlasFn: () => SpriteAtlas;
  private readonly hooks: ShaftHooks;
  private lampW = 100;
  private get atlas(): SpriteAtlas {
    return this.atlasFn();
  }

  constructor(atlas: () => SpriteAtlas, hooks: ShaftHooks) {
    this.atlasFn = atlas;
    this.hooks = hooks;
    this.bg = new Sprite(this.atlas.get(ASSETS.scene.shaft));
    this.sign = new Sprite(this.atlas.get(ASSETS.ui.woodSign));
    this.sign.anchor.set(0.5);
    this.signT = makeText(SHAFT_LABEL, 22, { bold: true, color: 0xfff1d0, align: 'center' });
    this.signT.anchor.set(0.5);
    this.back = button(this.atlas.get(ASSETS.ui.secondary), 'Back', 140, 48, 18);
    this.back.root.on('pointertap', () => hooks.onBack());
    this.root.addChild(this.bg, this.sign, this.signT, this.back.root);
  }

  private ensureLamps(n: number): void {
    while (this.lamps.length < n) {
      const root = new Container();
      const glow = new Sprite(this.atlas.get('glow'));
      glow.anchor.set(0.5);
      glow.blendMode = 'add';
      glow.tint = PALETTE.gold;
      const lamp = new Sprite(this.atlas.get(ASSETS.lamp.off));
      lamp.anchor.set(0.5);
      const num = makeText('1', 18, { bold: true, color: 0xfff1d0, align: 'center' });
      num.anchor.set(0.5);
      const stars: Sprite[] = [];
      for (let i = 0; i < 3; i++) {
        const st = new Sprite(this.atlas.get(ASSETS.ui.starEmpty));
        st.anchor.set(0.5);
        stars.push(st);
      }
      const tag = new Sprite(this.atlas.get(ASSETS.dangerTag));
      tag.anchor.set(0.5, 0);
      root.addChild(glow, lamp, num, ...stars, tag);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      const idx = this.lamps.length;
      // A2: a tap bounces the lamp, then opens the level card; a locked lamp just shivers.
      root.on('pointertap', () => {
        const lv = this.hooks.levels()[idx];
        const entry = this.lamps[idx];
        if (!lv || !entry) return;
        if (lv.locked) {
          entry.shakeAt = this.t;
          return;
        }
        entry.bounceAt = this.t;
        setTimeout(() => this.hooks.onPlay(lv.index), 190);
      });
      this.root.addChild(root);
      this.lamps.push({ root, lamp, num, stars, tag, glow, bounceAt: -1e9, shakeAt: -1e9 });
    }
  }

  layout(L: Layout): void {
    this.L = L;
    const { w, h } = L;
    cover(this.bg, w, h, 0.5);
    const sw = Math.min(300, w * 0.7);
    this.sign.width = sw;
    this.sign.height = sw * 0.62;
    this.sign.position.set(w / 2, L.insets.top + sw * 0.36);
    this.signT.style.fontSize = Math.round(sw * 0.085);
    this.signT.position.set(w / 2, this.sign.y + sw * 0.05);
    this.back.root.position.set(w / 2, h - L.insets.bottom - 46);
    this.placeLamps();
  }

  private placeLamps(): void {
    const L = this.L;
    if (!L) return;
    const levels = this.hooks.levels();
    this.ensureLamps(levels.length);
    const { w, h } = L;
    // Zigzag between the sign and the Back button; lamps sized so same-column neighbours never overlap.
    const sw = Math.min(300, w * 0.7);
    const top = L.insets.top + sw * 0.36 + sw * 0.31 + 30;
    const bottom = h - L.insets.bottom - 46 - 40;
    const step = Math.min(h * 0.16, (bottom - top) / Math.max(1, levels.length - 1));
    const lampW = Math.min(120, w * 0.3, step * 1.75);
    this.lampW = lampW;
    this.lamps.forEach((l, i) => {
      const lv = levels[i];
      l.root.visible = !!lv;
      if (!lv) return;
      const x = i % 2 === 0 ? w * 0.3 : w * 0.7;
      l.root.position.set(x, top + i * step);
      l.lamp.width = lampW;
      l.lamp.height = lampW;
      l.glow.width = lampW * 1.6;
      l.glow.height = lampW * 1.6;
      l.glow.position.set(-lampW * 0.12, lampW * 0.12);
      l.num.style.fontSize = Math.round(lampW * 0.16);
      l.num.position.set(-lampW * 0.12, lampW * 0.5);
      l.stars.forEach((st, k) => {
        st.width = lampW * 0.16;
        st.height = lampW * 0.16;
        st.position.set(-lampW * 0.12 + (k - 1) * lampW * 0.17, lampW * 0.66);
      });
      l.tag.width = lampW * 0.36;
      l.tag.height = lampW * 0.36;
      l.tag.position.set(lampW * 0.42, -lampW * 0.2);
    });
  }

  refreshTextures(): void {
    this.bg.texture = this.atlas.get(ASSETS.scene.shaft);
    this.sign.texture = this.atlas.get(ASSETS.ui.woodSign);
    this.back.bg.texture = this.atlas.get(ASSETS.ui.secondary);
  }

  update(dt: number): void {
    this.t += dt;
    const levels = this.hooks.levels();
    this.ensureLamps(levels.length);
    if (this.lamps.length !== levels.length) this.placeLamps();
    this.lamps.forEach((l, i) => {
      const lv = levels[i];
      if (!lv) return;
      const tex = this.atlas.get(lv.next ? ASSETS.lamp.next : lv.cleared ? ASSETS.lamp.lit : ASSETS.lamp.off);
      if (l.lamp.texture !== tex) l.lamp.texture = tex;
      l.num.text = String(lv.index + 1);
      l.stars.forEach((st, k) => {
        const t2 = this.atlas.get(k < lv.stars ? ASSETS.ui.starFull : ASSETS.ui.starEmpty);
        if (st.texture !== t2) st.texture = t2;
      });
      l.tag.visible = lv.hard;
      l.tag.rotation = lv.hard ? 0.08 * Math.sin(this.t / 900 + i) : 0;
      // A2: the next lamp pulses with its glow, locked lamps are cold grey, a tap bounces.
      l.glow.visible = lv.next;
      l.glow.alpha = 0.45 + 0.3 * Math.sin(this.t / 300);
      l.root.alpha = lv.locked ? 0.7 : 1;
      l.root.cursor = lv.locked ? 'default' : 'pointer';
      const cold = 0x6f7c92;
      l.lamp.tint = lv.locked ? cold : 0xffffff;
      l.num.tint = lv.locked ? 0xb0b8c8 : 0xffffff;
      for (const st of l.stars) st.alpha = lv.locked ? 0.35 : 1;
      let pulse = lv.next ? 1 + 0.08 * Math.sin(this.t / 300) : 1;
      const bt = (this.t - l.bounceAt) / 320;
      if (bt >= 0 && bt < 1) pulse *= 1 + 0.32 * Math.sin(bt * Math.PI) * (1 - bt * 0.4);
      const sk = (this.t - l.shakeAt) / 260;
      l.root.rotation = sk >= 0 && sk < 1 ? 0.08 * Math.sin(sk * Math.PI * 4) * (1 - sk) : 0;
      l.lamp.width = this.lampW * pulse;
      l.lamp.height = this.lampW * pulse;
    });
  }
}

// ---------------------------------------------------------------------------
// Level card (Systems Brief §2.2, §5): name, Target preview and the pre-level picker.

export interface LevelCardGoal {
  type: 'collect' | 'crush' | 'break' | 'survive' | 'clear' | 'rescue';
  gemType: number;
  target: number;
  phase: number;
}
export interface LevelCardInfo {
  index: number;
  name: string;
  hard: boolean;
  superHard: boolean;
  goals: LevelCardGoal[];
  stock: { shield: number; turbo: number; beam: number };
}
export type Pick = 'shield' | 'turbo' | 'beam';
export interface LevelCardHooks {
  onDig(picks: Pick[]): void;
  onBack(): void;
}

const PICK_SLOTS: Pick[] = ['shield', 'turbo', 'beam'];

export class LevelCardScreen {
  readonly root = new Container();
  private readonly bg: Sprite;
  private readonly shade = new Graphics();
  private readonly card: Sprite;
  private readonly title: Text;
  private readonly tag: Sprite;
  private readonly targetT: Text;
  private readonly goalRows: { icon: Sprite; text: Text }[] = [];
  private readonly pickT: Text;
  private readonly slots: { root: Container; bg: Sprite; icon: Sprite; count: Text; kind: Pick }[] = [];
  private readonly dig: Btn;
  private readonly back: Btn;
  private readonly hint: Text;
  private info: LevelCardInfo | null = null;
  private picks = new Set<Pick>();
  private t = 0;
  private readonly atlasFn: () => SpriteAtlas;
  private readonly hooks: LevelCardHooks;
  private get atlas(): SpriteAtlas {
    return this.atlasFn();
  }

  constructor(atlas: () => SpriteAtlas, hooks: LevelCardHooks) {
    this.atlasFn = atlas;
    this.hooks = hooks;
    void this.hooks;
    this.bg = new Sprite(this.atlas.get(ASSETS.scene.shaft));
    this.card = new Sprite(this.atlas.get(ASSETS.ui.card));
    this.card.anchor.set(0.5);
    this.title = makeText('', 26, { bold: true, color: PALETTE.uiFrame, align: 'center' });
    this.title.anchor.set(0.5);
    this.tag = new Sprite(this.atlas.get(ASSETS.dangerTag));
    this.tag.anchor.set(0.5);
    this.targetT = makeText('TARGET', 13, { bold: true, color: PALETTE.uiFrame });
    this.targetT.anchor.set(0.5);
    for (let i = 0; i < 3; i++) {
      const icon = new Sprite(this.atlas.get(ASSETS.goalIcon('collect')));
      icon.anchor.set(0.5);
      const text = makeText('', 15, { bold: true, color: PALETTE.uiFrame });
      text.anchor.set(0, 0.5);
      this.goalRows.push({ icon, text });
    }
    this.pickT = makeText('BRING', 13, { bold: true, color: PALETTE.uiFrame });
    this.pickT.anchor.set(0.5);
    for (const kind of PICK_SLOTS) {
      const root = new Container();
      const bg = new Sprite(this.atlas.get(ASSETS.ui.iconRound));
      bg.anchor.set(0.5);
      const icon = new Sprite(this.atlas.get(ASSETS.kit[kind]));
      icon.anchor.set(0.5);
      const count = makeText('', 12, { bold: true, color: 0xffffff, align: 'center' });
      count.anchor.set(0.5, 0);
      root.addChild(bg, icon, count);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      root.on('pointertap', () => this.toggle(kind));
      this.slots.push({ root, bg, icon, count, kind });
    }
    this.dig = button(this.atlas.get(ASSETS.ui.cta), 'Dig', 200, 56, 22, 0x2a1608);
    this.dig.root.on('pointertap', () => hooks.onDig([...this.picks]));
    this.back = button(this.atlas.get(ASSETS.ui.secondary), 'Back', 120, 44, 16);
    this.back.root.on('pointertap', () => hooks.onBack());
    this.hint = makeText('', 12, { color: PALETTE.uiFrame, align: 'center' });
    this.hint.anchor.set(0.5);
    this.root.addChild(this.bg, this.shade, this.card, this.title, this.tag, this.targetT, ...this.goalRows.flatMap((r) => [r.icon, r.text]), this.pickT, ...this.slots.map((s) => s.root), this.hint, this.dig.root, this.back.root);
  }

  show(info: LevelCardInfo): void {
    this.info = info;
    this.picks.clear();
    this.refreshTextures();
  }

  private toggle(kind: Pick): void {
    if (!this.info) return;
    if (this.picks.has(kind)) this.picks.delete(kind);
    else if (this.info.stock[kind] > 0) this.picks.add(kind);
    this.refreshSlots();
  }

  refreshTextures(): void {
    this.bg.texture = this.atlas.get(ASSETS.scene.shaft);
    this.card.texture = this.atlas.get(ASSETS.ui.card);
    this.tag.texture = this.atlas.get(ASSETS.dangerTag);
    this.dig.bg.texture = this.atlas.get(ASSETS.ui.cta);
    this.back.bg.texture = this.atlas.get(ASSETS.ui.secondary);
    const info = this.info;
    if (!info) return;
    this.title.text = `${info.index + 1} · ${info.name}`;
    this.tag.visible = info.hard || info.superHard;
    const goals = info.goals.slice(0, 3);
    this.goalRows.forEach((r, i) => {
      const g = goals[i];
      r.icon.visible = r.text.visible = !!g;
      if (!g) return;
      r.icon.texture = this.atlas.get(g.type === 'collect' && g.gemType >= 0 ? `gem.${g.gemType % PALETTE.gems.length}` : ASSETS.goalIcon(g.type));
      const what = { collect: g.gemType >= 0 ? `${g.target} ${GEM_LABEL[g.gemType]}` : `${g.target} gems`, crush: `Crush ${g.target}`, break: `Break ${g.target}`, survive: `Survive ${Math.round((g.target * 80) / 1000)} s`, clear: `Clear the marked pocket`, rescue: `Find the buried charm` }[g.type];
      r.text.text = goals.length > 1 && info.goals.some((x) => x.phase > 0) ? `${g.phase + 1}. ${what}` : what;
    });
    this.hint.text = info.goals.length ? '' : 'Collect every gem or clear the creatures';
    this.refreshSlots();
  }

  private refreshSlots(): void {
    const info = this.info;
    if (!info) return;
    for (const s of this.slots) {
      const n = info.stock[s.kind];
      const on = this.picks.has(s.kind);
      s.bg.texture = this.atlas.get(ASSETS.ui.iconRound);
      s.icon.texture = this.atlas.get(n > 0 ? ASSETS.kit[s.kind] : ASSETS.kit.lock);
      s.icon.alpha = n > 0 ? 1 : 0.5;
      s.count.text = n > 0 ? (on ? `${s.kind} ✓` : `×${n}`) : 'shop soon';
      s.root.scale.set(on ? 1.12 : 1);
      s.bg.tint = on ? 0xffe9a0 : 0xffffff;
    }
  }

  layout(L: Layout): void {
    const { w, h } = L;
    cover(this.bg, w, h, 0.5);
    this.shade.clear();
    this.shade.rect(0, 0, w, h).fill({ color: 0x000000, alpha: 0.35 });
    const cw = Math.min(360, w - 32);
    const ch = Math.min(h - L.insets.top - L.insets.bottom - 40, cw * 1.3);
    const cx = w / 2;
    const cy = L.insets.top + 20 + ch / 2;
    this.card.position.set(cx, cy);
    this.card.width = cw;
    this.card.height = ch;
    const top = cy - ch / 2;
    this.title.style.fontSize = Math.round(cw * 0.065);
    this.title.position.set(cx, top + ch * 0.2);
    this.tag.position.set(cx + cw * 0.34, top + ch * 0.13);
    this.tag.scale.set((cw * 0.14) / 64);
    this.targetT.position.set(cx, top + ch * 0.29);
    this.goalRows.forEach((r, i) => {
      const y = top + ch * (0.35 + i * 0.08);
      r.icon.position.set(cx - cw * 0.3, y);
      r.icon.scale.set((cw * 0.075) / 64);
      r.text.position.set(cx - cw * 0.22, y);
      r.text.style.fontSize = Math.round(cw * 0.042);
    });
    this.pickT.position.set(cx, top + ch * 0.62);
    this.slots.forEach((s, i) => {
      s.root.position.set(cx + (i - 1) * cw * 0.28, top + ch * 0.73);
      const r = cw * 0.1;
      s.bg.width = s.bg.height = r * 2;
      s.icon.scale.set((r * 1.5) / 64);
      s.count.position.set(0, r + 2);
      s.count.style.fontSize = Math.round(cw * 0.035);
    });
    this.hint.position.set(cx, top + ch * 0.86);
    this.dig.root.position.set(cx, top + ch * 0.93);
    this.back.root.position.set(cx, Math.min(h - L.insets.bottom - 36, top + ch + 44));
  }

  update(dt: number): void {
    this.t += dt;
    const s = 1 + 0.02 * Math.sin(this.t / 500);
    if (this.dig.root.scale.x > 0.99) this.dig.root.scale.set(s);
  }
}

const GEM_LABEL = ['rubies', 'emeralds', 'sapphires', 'amethysts', 'topaz'];
